import { useCallback, useEffect, useState } from 'react';
import { useLatestRequest } from '../../hooks/useLatestRequest';

export interface ReportState<T> {
  data: T | null;
  loading: boolean;
  /** The thrown error, for `apErrorMessage`. Never shown as it is. */
  error: unknown;
  reload: () => void;
}

/**
 * Loads one report and re-loads it when `deps` change. Overlapping requests are last-one-wins
 * (a slow answer to an earlier date must not overwrite the answer to the later one:
 * `useLatestRequest`, TECH_DEBT #46), and a failure clears the data so a report never shows
 * figures from a request that has since been refused.
 */
export function useReport<T>(load: () => Promise<T>, deps: ReadonlyArray<unknown>): ReportState<T> {
  const beginRequest = useLatestRequest();
  const [state, setState] = useState<{ data: T | null; loading: boolean; error: unknown }>({
    data: null,
    loading: true,
    error: null,
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const isCurrent = beginRequest();
    setState((prev) => ({ ...prev, loading: true, error: null }));
    load().then(
      (data) => {
        if (isCurrent()) setState({ data, loading: false, error: null });
      },
      (error) => {
        if (isCurrent()) setState({ data: null, loading: false, error: error ?? new Error('failed') });
      }
    );
    // The caller lists what the request depends on; `load` is rebuilt every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, reload };
}
